import { Box, Button, Container, FormLabel, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { postUser } from '../requests';
import { userInfoType } from '../types';
import { useSnackbar } from '../../../components/hooks/useSnackbar';

const styles = {
  formLabel: {
    color: '#000',
    textAlign: 'left',
    fontFamily: 'YuGothic',
    fontSize: 16,
    lineHeight: '24px',

    '.MuiFormLabel-asterisk': {
      fontWeight: 'bold',
      color: '#ff0000',
    },
  },
  confirmValue: {
    color: '#000',
    textAlign: 'left',
    fontFamily: 'YuGothic',
    fontSize: 16,
    lineHeight: '24px',
    padding: '0 6px',
  },
  textField: {
    height: 30,
    padding: '0 16px',
  },
  button: {
    backgroundColor: '#2980B9',
    minWidth: 120,
    height: 34,
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
    fontFamily: 'YuGothic',
    padding: '0 16px',
    '&:hover': {
      background: '#2980B9',
    },
  },
  outline: {
    backgroundColor: '#fff',
    border: '1px solid #2980B9',
    color: '#2980B9',
    '&:hover': {
      background: '#fff',
    },
  },
};

// バリデーションルール
const schema = yup.object({
  username: yup.string().required('必須です'),
  company_name: yup.string().required('必須です'),
  department: yup.string().optional(),
  position: yup.string().optional(),
  address: yup.string().optional(),
  phone: yup.string().optional(),
  email: yup.string().optional(),
});

export const ApplicationPage = () => {
  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
  });
  const { user } = useAuthenticator(context => [context.route]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [data, setData] = React.useState<userInfoType>({} as userInfoType);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { CustomSnackbar, setWindowAlert } = useSnackbar();

  const toConfirm = (data: any) => {
    setData({
      ...data,
      cognito_username: user?.username,
      email: user.attributes?.email,
      default_email: user.attributes?.email,
      office_address: data.address || '未入力',
      position: data.position || '未入力',
    });
    setShowConfirm(true);
  };

  const toContact = () => {
    setShowConfirm(false);
  };

  const onSubmit = async () => {
    if (!data) {
      return;
    }
    setIsLoading(true);

    const isSent = await postUser({ data: { ...data, privacy: true } });

    if (isSent) {
      alert(t('pages.rootApplication.submit.alert.ok'));
      navigate('/prediction/map');
    } else {
      setWindowAlert(t('pages.rootApplication.submit.alert.ng'), 'error');
    }
    setIsLoading(false);
  };

  const { errors } = formState;
  return (
    <>
      <Box style={{ margin: '10vw', width: '100%', textAlign: 'center' }}>
        {CustomSnackbar}
        <Container
          sx={{
            maxWidth: 1200,
            height: '100%',
            margin: '0 auto',
            padding: '36px 0',
            backgroundColor: '#fff',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: 21,
              fontFamily: 'YuGothic',
              fontWeight: 500,
              lineHeight: '36px',
              color: '#000',
              borderBottom: '1px solid #007B43',
              display: 'inline-block',
            }}
          >
            {t('pages.rootApplication.title')}
          </Typography>

          <Stack spacing={3} sx={{ width: '60vw', margin: '0 auto', padding: '16px 0 24px' }}>
            {!showConfirm && (
              // Screen for Editing
              <>
                <Stack spacing={1}>
                  <FormLabel required sx={styles.formLabel}>
                    {t('pages.rootApplication.fullName')}
                  </FormLabel>
                  <TextField
                    required
                    {...register('username')}
                    error={'username' in errors}
                    helperText={''}
                    inputProps={{
                      style: styles.textField,
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('common.label.email')}</FormLabel>
                  <TextField
                    required
                    type="email"
                    value={(!!user.attributes && user.attributes.email) || ''}
                    {...register('email')}
                    error={'email' in errors}
                    inputProps={{
                      style: { ...styles.textField, backgroundColor: '#ccc' },
                    }}
                    disabled={true}
                  />
                </Stack>

                <Stack spacing={1}>
                  <FormLabel required sx={styles.formLabel}>
                    {t('pages.rootApplication.companyName')}
                  </FormLabel>
                  <TextField
                    required
                    {...register('company_name')}
                    error={'company_name' in errors}
                    helperText={errors.company_name?.message}
                    inputProps={{
                      style: styles.textField,
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <FormLabel required sx={styles.formLabel}>
                    {t('pages.rootApplication.department')}
                  </FormLabel>
                  <TextField
                    {...register('department')}
                    error={'department' in errors}
                    helperText={errors.department?.message}
                    inputProps={{
                      style: styles.textField,
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.position')}</FormLabel>
                  <TextField
                    {...register('position')}
                    error={'position' in errors}
                    helperText={errors.position?.message}
                    inputProps={{
                      style: styles.textField,
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.address')}</FormLabel>
                  <TextField
                    {...register('address')}
                    name="address"
                    error={'address' in errors}
                    helperText={errors.address?.message}
                    inputProps={{
                      style: styles.textField,
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.phone')}</FormLabel>
                  <TextField
                    {...register('phone')}
                    name="phone"
                    error={'phone' in errors}
                    helperText={errors.phone?.message}
                    inputProps={{
                      style: styles.textField,
                    }}
                  />
                </Stack>

                <Stack spacing={1} style={{ alignItems: 'center' }}>
                  <Button
                    onClick={handleSubmit(toConfirm)}
                    sx={{
                      ...styles.button,
                      backgroundColor: '#2980B9',
                    }}
                  >
                    {t('pages.rootApplication.button.confirm')}
                  </Button>
                </Stack>
              </>
            )}

            {/* 確認画面 */}

            {!!showConfirm && (
              <>
                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.fullName')}</FormLabel>
                  <Typography sx={styles.confirmValue}>{data?.username}</Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('common.label.email')}</FormLabel>
                  <Typography sx={styles.confirmValue}>
                    {(!!user.attributes && user.attributes.email) || ''}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>
                    {t('pages.rootApplication.companyName')}
                  </FormLabel>
                  <Typography sx={styles.confirmValue}>{data?.company_name}</Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>
                    {t('pages.rootApplication.department')}
                  </FormLabel>
                  <Typography sx={styles.confirmValue}>{data?.department}</Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.position')}</FormLabel>
                  <Typography sx={styles.confirmValue}>{data?.position}</Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.address')}</FormLabel>
                  <Typography sx={styles.confirmValue}>{data?.address}</Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormLabel sx={styles.formLabel}>{t('pages.rootApplication.phone')}</FormLabel>
                  <Typography sx={styles.confirmValue}>{data?.phone}</Typography>
                </Stack>

                {/* 利用規約のための文言 */}
                <Stack spacing={1}>
                  <div style={{ textAlign: 'left' }}>
                    <Trans
                      i18nKey="pages.rootApplication.termsOfUse.regardedAsAgreement"
                      components={[
                        <a
                          href="https://drive.google.com/file/d/1KapVBdHIpGiYdgLy9SI8DnsuBuCwcg8w/view"
                          style={{
                            color: '#68a4d9',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                          target="_blank"
                        />,
                        <a
                          href="https://drive.google.com/file/d/1KbzIo51t9kmEvri5IAxOfkVKsyUuZZVJ/view"
                          style={{
                            color: '#68a4d9',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                          target="_blank"
                        />,
                      ]}
                      values={{
                        termsOfUse: t('pages.rootApplication.termsOfUse.termsOfUse'),
                        privacy: t('pages.rootApplication.termsOfUse.privacyPolicy'),
                      }}
                    />
                  </div>
                </Stack>

                <Stack
                  spacing={1}
                  style={{ justifyContent: 'center', marginTop: '60px' }}
                  direction="row"
                >
                  <Button disabled={isLoading} onClick={onSubmit} sx={styles.button}>
                    {t('pages.rootApplication.button.register')}
                  </Button>
                  <Button
                    disabled={isLoading}
                    onClick={toContact}
                    variant="outlined"
                    sx={{ ...styles.button, ...styles.outline }}
                    style={{ marginLeft: '48px' }}
                  >
                    {t('pages.rootApplication.button.edit')}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
};
